import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Advent of Code Day 14 – Disk Fragmentation",
  "description": "",
  "published": "2017-12-14T00:00:00.000Z",
  "redirect_from": ["/blog/advent-code-day-14-disk-fragmentation/swizec/7965"],
  "categories": "Front End, python, Technical",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2017/12/carbon-day14-star1-1024x602.png",
        "alt": "Day 14 was trivial after I solved Day 10"
      }}></img></p>
    <lite-youtube {...{
      "videoid": "e9duNOVdHFk",
      "videostartat": "0"
    }}></lite-youtube>
    <p>{`Advent of Code Day 14 was a strange, strange thing. At first, it made no sense. Something about disk fragmentation and encoding addresses as knot hashes and whatnot.`}</p>
    <p>{`What the hell is a knot hash? O.o`}</p>
    <p>{`Turns out it's from Advent of Code Day 10. Conveniently called "knot hash". On Day 10, you build a general hashing function following this schema 👇`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2017/12/screen_shot_2017-12-14_at_08.50.05.png",
        "alt": null
      }}></img></p>
    <p>{`Don't worry, that image didn't tell me anything either.`}</p>
    <p>{`AoC's description of the process makes more sense`}</p>
    <blockquote>
      <p parentName="blockquote">{`To achieve this, begin with a list of numbers from 0 to 255, a current position which begins at 0 (the first element in the list), a skip size (which starts at 0), and a sequence of lengths (your puzzle input). Then, for each length:`}</p>
      <p parentName="blockquote">{`Reverse the order of that length of elements in the list, starting with the element at the current position.`}</p>
      <p parentName="blockquote">{`Move the current position forward by that length plus the skip size.`}</p>
      <p parentName="blockquote">{`Increase the skip size by one.`}</p>
      <p parentName="blockquote">{`The list is circular; if the current position and the length try to reverse elements beyond the end of the list, the operation reverses using as many extra elements as it needs from the front of the list. If the current position moves past the end of the list, it wraps around to the front. Lengths larger than the size of the list are invalid.`}</p>
    </blockquote>
    <p>{`Okay. So that's difficult to follow, and it's kind of annoying to build, but Day 10 boils down to what we do best in our day-to-day programming: Follow instructions to implement a random piece of business logic.`}</p>
    <h2 {...{
      "id": "day-10-star-1"
    }}>{`Day 10, star 1`}</h2>
    <p>{`The puzzle is the instructions. We just gotta write the correct code. In Python, mine looks like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-python"
      }}>{`def knot_1round(pos, skip, lengths, input):
    for length in lengths:
        if pos+length > len(input):
            wrap = (pos+length)%len(input)
        else:
            wrap = 0

        nums = input[pos:pos+length] + input[:wrap]
        nums = list(reversed(nums))

        if wrap > 0:
            input = nums[-wrap:] + input[wrap:pos] + nums[:-wrap] + input[pos+length:]
        else:
            input = input[:pos] + nums + input[pos+length:]

        pos = (pos+length+skip)%len(input)
        skip += 1

    return pos, skip, input
`}</code></pre>
    <p>{`Boring, predictable, nothing special. Go through the list of numbers, use input lengths, do the knot hashing. The neat part of this code is Python's spectacular support for list slicing and dicing.`}</p>
    <p>{`You can use the `}<inlineCode parentName="p">{`:`}</inlineCode>{` operator to get different parts of a list. Left side means "up to" and right side means "after". You can use negative numbers.`}</p>
    <p>{`Dealing with the circular wraparound part was mindbendy and a bit tedious. You can't just cut the list and put it together. You have to use a bunch of different bits and sections.`}</p>
    <h2 {...{
      "id": "day-10-star-2"
    }}>{`Day 10, star 2`}</h2>
    <p>{`For the second part, we assembled 64 single rounds of knot hashing into a full knot hash. Again, just following instructions and most of the hard work coming down to understanding those instructions.`}</p>
    <p>{`No fun insights needed 😴`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-python"
      }}>{`def knot(input):
    lengths = [ord(str(c)) for c in input] + [17, 31, 73, 47, 23]

    pos = 0
    skip = 0
    numbers = range(0, 256)

    for i in xrange(64):
        pos, skip, numbers = knot_1round(pos, skip, lengths, numbers)

    blocks = [reduce(lambda b, n: b^n, numbers[i:i+16]) for i in xrange(0, len(numbers), 16)]

    print numbers[0:16]

    return "".join(["%0.2X" % c for c in blocks])
`}</code></pre>
    <p>{`You take the input, turn each character into its ASCII number, add a static salt thing `}<inlineCode parentName="p">{`[17, 31, 73, 47, 23]`}</inlineCode>{`, then run the knot round 64 times. Make sure each round feeds the current position and skip size into the next one.`}</p>
    <p>{`In the end, you take that result and parse it back into a hexadecimal string.`}</p>
    <p>{`So an empty string input produces `}<inlineCode parentName="p">{`a2582a3a0e66e6e86e3812dcb672a272`}</inlineCode>{`, for example.`}</p>
    <p><em parentName="p">{`shrug emoji`}</em></p>
    <h2 {...{
      "id": "day-14-star-1"
    }}>{`Day 14, star 1`}</h2>
    <p>{`With our general knot hashing function in hand, we could take on Day 14. Only took me 70 minutes to get here 😂`}</p>
    <p>{`But Day 14, the first part at least, was trivial now that the hashing made sense.`}</p>
    <p>{`The goal is to figure out how many blocks of a disk space are occupied. You are given an input string, and you compute it into 128 knot hashes, then you transform those knot hashes into binary, and count how many 1's there are.`}</p>
    <p>{`Again, in Python, it looks like this 👇`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-python"
      }}>{`inputs = [input+"-"+str(i) for i in xrange(128)]

def star1(inputs):
    used = 0

    for input in inputs:
        hash = knot(input)
        used += sum(bin(int(c, 16))[2:].count('1') for c in hash)

    return used
`}</code></pre>
    <p>{`Make a list of 128 inputs, walk through it, calculate hashes, transform them into binary, count the 1's and sum it up. Done.`}</p>
    <p>{`That was easy.`}</p>
    <p>{`Then `}<strong parentName="p">{`Star 2`}</strong>{` came along, and it asked to count `}<em parentName="p">{`regions`}</em>{`. That is adjacent 1's that are touching.`}</p>
    <p>{`So I said, “Screw this, I'm tired,” and went to bed. 😴`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      